import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import LevelSelection from './pages/LevelSelection';
import PlacementQuiz from './pages/PlacementQuiz';
import LevelDashboard from './pages/LevelDashboard';
import ModuleExercises from './pages/ModuleExercises';
import ExerciseInterface from './pages/ExerciseInterface';
import ExerciseResults from './pages/ExerciseResults';
import ProgressPage from './pages/ProgressPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import DailyPractice from './pages/DailyPractice';
import ExamDateSelection from './pages/ExamDateSelection';
import PartnersLanding from './pages/PartnersLanding';
import PartnerDashboard from './pages/PartnerDashboard';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// API instance with credentials
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Track referral code from URL
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('delf_ref_code', ref);
      localStorage.setItem('delf_ref_time', Date.now().toString());
      // Track click
      api.post('/partners/track-click', { ref }).catch(() => {});
    }
  }, [location.search]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LevelSelection />} />
      <Route path="/levels" element={<Navigate to="/" replace />} />
      <Route path="/quiz" element={<PlacementQuiz />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/partners" element={<PartnersLanding />} />
      <Route path="/partners/dashboard" element={<PartnerDashboard />} />
      
      {/* Protected Routes */}
      <Route path="/level/:level" element={<ProtectedRoute><LevelDashboard /></ProtectedRoute>} />
      <Route path="/level/:level/:module" element={<ProtectedRoute><ModuleExercises /></ProtectedRoute>} />
      <Route path="/exercise/:exerciseId" element={<ProtectedRoute><ExerciseInterface /></ProtectedRoute>} />
      <Route path="/results/:attemptId" element={<ProtectedRoute><ExerciseResults /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      <Route path="/daily" element={<ProtectedRoute><DailyPractice /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/exam-date" element={<ProtectedRoute><ExamDateSelection /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
